'use strict'

const _ = require('lodash')
const {createResolver} = require('./resolver')
const {pack} = require('./packer')
const baseDefinitions = require('./baseDefinitions.json')

/**
 * returns wrapper to handle schemas (properties/definitions)
 * exposes API to manipulate shcema
 * @param (customDefinitions) object containing all custom definitions
 * @returns {{set: *, get: *, create: *, check: *, unset: *}}
 */
const createDriver = customDefinitions => {
    const RESTRICTIONS = ['minimum', 'maximum', 'multipleOf', 'minLength', 'maxLength']

    /**
     * convert array to object if is array
     * * @param (schemas) array or object of schemas
     */
    const convertSchemasArrayToObject = schemas => Array.isArray(schemas) ? _.assign({}, ...schemas) : schemas

    let usingCustomDefinitions = convertSchemasArrayToObject(customDefinitions)
    const resolver = createResolver(baseDefinitions, usingCustomDefinitions)
    let isArray = false
    let name = 'prop'
    let schema = {}
    let resolved = {}

    /**
     * update data after schema change
     */
    const updateBySchema = () => {
        name = _.head(_.keys(schema))
        resolved = resolver.resolve(schema)
        isArray = _.get(resolved, [name, 'type']) === 'array'
    }

    /**
     * update data after resolved change
     */
    const updateByResolved = () => {
        name = _.head(_.keys(resolved))
        schema = pack(resolved)
        isArray = _.get(resolved, [name, 'type']) === 'array'
    }

    /**
     * update custom definitions to be used by resolver
     * @param (newCustomDefinitions) object containing all custom definitions
     */
    const setCustomDefinitions = newCustomDefinitions => {
        usingCustomDefinitions = convertSchemasArrayToObject(newCustomDefinitions)
        resolver.update(baseDefinitions, usingCustomDefinitions)
        updateBySchema()
    }

    /**
     * when changing schema to array, need to clean kesy that do not exist under array item
     * @param (data) schema data (not array)
     * @returns {*} cleaned schema data to insert to array item
     */
    const transformDataToItemsData = data => {
        const result = _.cloneDeep(data)
        const extendData = _.pick(_.get(result, ['allOf', 1], {}), RESTRICTIONS)
        _.set(result, ['allOf', 1], extendData)
        return result
    }

    /**
     * set new data for key in resolved data, update schema after change
     * @param (key) key in resolved data
     * @param (value) new value for key in resolved data
     */
    const setResolvedValue = (key, value) => {
        if (isArray) {
            _.set(resolved, [name, 'items', key], value)
        } else {
            _.set(resolved, [name, key], value)
        }
        updateByResolved()
    }

    /**
     * remove key from resolved data, update schema after change
     * @param (key) key in resolved data to remove
     */
    const unsetResolvedValue = key => {
        if (isArray) {
            _.unset(resolved, [name, 'items', key])
        } else {
            _.unset(resolved, [name, key])
        }
        updateByResolved()
    }

    /**
     * update multiple resolved values specified in object
     * @param (valuesObj) pbject containing key-value pairs, for each key set resolved value
     */
    const setResolvedValues = valuesObj => {
        _.forOwn(valuesObj, (value, key) => setResolvedValue(key, value))
    }

    /**
     * set new data for key in resolved data, update schema after change
     * unlike setResolvedValue, this function deals with values that exist in the root of the schema
     * like: description, title, default, ...
     * @param (key) key in resolved data
     * @param (value) new value for key in resolved data
     */
    const setResolvedRootValue = (key, value) => {
        _.set(resolved, [name, key], value)
        updateByResolved()
    }

    /**
     * get specific key value from resolved data
     * @param (key) key in resolved data
     * @returns {*} data of the key requested
     */
    const getResolvedValue = key => isArray ? _.get(resolved, [name, 'items', key]) : _.get(resolved, [name, key])

    /**
     * * get multiple keys values from resolved data
     * @param (keys) keys in resolved data
     * @returns {*} object containing all requested keys and their values
     */
    const getResolvedValues = keys => isArray ? _.pick(_.get(resolved, [name, 'items']), keys) : _.pick(resolved[name], keys)

    /**
     * get data from resolved data by key
     * unlike getResolvedValue, this function deals with values that exist in the root of the schema
     * like: description, title, default, ...
     * @param (key) key in resolved data
     * @returns {*} data of the key requested
     */
    const getResolvedRootValue = key => _.get(resolved, [name, key])

    /**
     * get current schema
     * @returns {[name]: {*}} current schema
     */
    const getSchema = () => schema

    /**
     * set new schema and update data
     * @param (newSchema) new schema for driver to use
     */
    const setSchema = newSchema => {
        schema = _.assign({}, newSchema)
        updateBySchema()
    }

    /**
     * get data of the schema
     * @returns {*} data of current schema (under [name] key)
     */
    const getData = () => schema[name]

    /**
     * set data of the current schema (not changing only name), update data
     * @param (newData) new data to insert to schema under [name] key
     */
    const setData = newData => {
        schema = {[name]: _.assign({}, newData)}
        updateBySchema()
    }

    /**
     * get resolved schema
     * @returns {[name]: {*}} resolved schema (1 level resolving)
     */
    const getResolvedSchema = () => resolved

    /**
     * set resolved data, update schema accordingly
     * @param (newResolvedSchema) resolved schema to set data by
     */
    const setResolvedSchema = newResolvedSchema => {
        resolved = _.assign({}, newResolvedSchema)
        updateByResolved()
    }

    /**
     *  get data of resolved schema
     * @returns {*} data of current resolved schema (under [name] key)
     */
    const getResolvedData = () => resolved[name]

    /**
     * set data of the current resolved schema (not changing only name), update data
     * @param (newResolvedData) new data to insert to resolved schema under [name] key
     */
    const setResolvedData = newResolvedData => {
        resolved = {[name]: _.assign({}, newResolvedData)}
        updateByResolved()
    }

    /**
     * get schema name
     * @returns {string} first key in schema object - its name
     */
    const getName = () => name

    /**
     * set new name for schema, move data under new name
     * @param (newName) string
     */
    const setName = newName => setSchema({[newName]: schema[name]})

    /**
     * get title of schema
     * @returns {string}
     */
    const getTitle = () => getResolvedRootValue('title')

    /**
     * set new title for schema
     * @param (newTitle) string
     */
    const setTitle = newTitle => setResolvedRootValue('title', newTitle)

    /**
     * * get description of schema
     * @returns {string}
     */
    const getDescription = () => getResolvedRootValue('description')

    /**
     * set new description for schema
     * @param (newDescription) string
     */
    const setDescription = newDescription => setResolvedRootValue('description', newDescription)

    /**
     * get schema default value
     * @returns {*} default value of schema, can be any value (according to definition)
     */
    const getDefault = () => getResolvedRootValue('default')

    /**
     * set schema default value
     * @param (newDefault) any value type (according to definition)
     */
    const setDefault = newDefault => setResolvedRootValue('default', newDefault)

    /**
     * get restriction of schema, only [number, text] currently have restrictions
     * return requested restriction key value if exists
     * @param (restriction) restriction name to get its value, can be any of RESTRICTIONS array
     * @returns {string|number|undefined} value of restriction, undefined if not exists
     */
    const getRestriction = restriction => _.includes(RESTRICTIONS, restriction) ? getResolvedValue(restriction) : undefined

    /**
     * set value for a specific restriction
     * if restriction not exists create it
     * if restriction does not exists in RESTRICTIONS array, ignore it
     * @param (restriction) restriction name, can be any of RESTRICTIONS array
     * @param (value) new value to set to restriction key
     */
    const setRestriction = (restriction, value) => setResolvedValue(restriction, value)

    /**
     * remove restriction from schema
     * if restriction does not exists in RESTRICTIONS array, ignore it
     * @param (restriction) restriction name to remove
     */
    const unsetRestriction = restriction => _.includes(RESTRICTIONS, restriction) ? unsetResolvedValue(restriction) : undefined

    /**
     * clear all restrictions from schema
     * removes keys that exists in RESTRICTIONS array
     */
    const clearRestrictions = () => {
        _.forEach(RESTRICTIONS, restriction => unsetRestriction(restriction))
    }

    /**
     * get restrictions of schema, only [number, text] currently have restrictions
     * return requested restrictions keys and values if exists
     * @returns {*} object containing key-value pairs of restrictions and their values
     */
    const getRestrictions = () => getResolvedValues(RESTRICTIONS)

    /**
     * set restrictions of schema
     * clear all existing restrictions and sets the new ones
     * ignores values that do not exists in RESTRICTIONS array
     * @param (newRestrictions) object containing key-value pairs of restriction names and their values
     */
    const setRestrictions = newRestrictions => {
        clearRestrictions()
        setResolvedValues(_.omitBy(newRestrictions, (value, key) => !_.includes(RESTRICTIONS, key)))
    }

    /**
     * get schema type
     * can be any of the following json-schema valid types: string, number, boolean, array, object
     * @returns {string} the type of schema
     */
    const getType = () => getResolvedRootValue('type')

    /**
     * get the definition id used to create this property
     * @returns {string} schema definition id
     */
    const getDefinitionID = () => getResolvedValue('$id')

    /**
     * get definition origin
     * @returns {string} base/custom
     */
    const getOrigin = () => getResolvedValue('origin')

    /**
     * get properties of schema if exists
     * @returns {*} object containing properties of current schema or undefined
     */
    const getProperties = () => getResolvedValue('properties')

    /**
     * check if this property is of type array
     * @returns {boolean} is schema of type array
     */
    const getIsArray = () => isArray

    /**
     * change schema structure to array or single, update data after change
     * @param (newIsArray) boolean, true for array, false for single
     */
    const setIsArray = newIsArray => {
        if (isArray === newIsArray) {
            return
        }

        if (newIsArray) {
            setData({
                allOf: [
                    {
                        type: 'array',
                        title: getTitle(),
                        description: getDescription(),
                        default: [getDefault()],
                        items: transformDataToItemsData(getData())
                    }
                ]
            })
        } else {
            setData({
                allOf: [
                    {
                        $ref: getDefinitionID()
                    },
                    _.assign({
                        title: getTitle(),
                        description: getDescription(),
                        default: _.head(getDefault())
                    },
                    getRestrictions())
                ]
            })
        }
        updateBySchema()
    }

    // TODO:: push default data to prop from parent so the driver can be used for values as well
    /**
     * create driver from property of current schema by name
     * @param (propertyName) name of property to wrap in driver
     * @returns {{set: *, get: *, create: *, check: *, unset: *}} driver of property
     */
    const createPropertyDriver = propertyName => {
        const properties = getProperties()
        const driver = createDriver(usingCustomDefinitions)
        driver.set.schema({[propertyName]: properties[propertyName]})
        return driver
    }

    /**
     * check if schema is using specific definition
     * recursive check in properties if they us definition
     * @param (refId) string, reference id of definition to check
     * @param (checkedIDs) array of already checked definitions, to avoid rechecking and endless loops
     * @returns {boolean} true if schema uses refferenced definition, false if not
     */
    const isUsingDefinition = (refId, checkedIDs = []) => {
        const definitionID = getDefinitionID()
        if (definitionID === refId) {
            return true
        }
        if (_.includes(checkedIDs, definitionID)) {
            return false
        }
        checkedIDs.push(definitionID)

        const properties = getProperties()
        if (properties) {
            return !_.every(properties, (value, key) => {
                const driver = createPropertyDriver(key)
                return !driver.check.isUsingDefinition(refId, checkedIDs)
            })
        }
        return false
    }

    /**
     * get name of definition by its $id, find in both base and custom definitions
     * Note: useing name instead of the unique $id to be able to display the name in the code completion
     * @returns {string} definition name
     */
    const getDefinitionName = () => {
        const defID = getDefinitionID()
        return _.findKey(baseDefinitions, ['$id', defID]) || _.findKey(usingCustomDefinitions, ['$id', defID]) || defID
    }

    /**
     * driver API
     */
    return {
        set: {
            schema: setSchema,
            data: setData,
            resolvedSchema: setResolvedSchema,
            resolvedData: setResolvedData,
            name: setName,
            title: setTitle,
            description: setDescription,
            default: setDefault,
            restrictions: setRestrictions,
            restriction: setRestriction,
            isArray: setIsArray,
            customDefinitions: setCustomDefinitions
        },
        unset: {
            restriction: unsetRestriction
        },
        get: {
            schema: getSchema,
            data: getData,
            resolvedSchema: getResolvedSchema,
            resolvedData: getResolvedData,
            name: getName,
            title: getTitle,
            description: getDescription,
            default: getDefault,
            restrictions: getRestrictions,
            restriction: getRestriction,
            type: getType,
            definitionID: getDefinitionID,
            definitionName: getDefinitionName,
            properties: getProperties,
            origin: getOrigin
        },
        check: {
            isArray: getIsArray,
            isUsingDefinition
        },
        create: {
            propertyDriver: createPropertyDriver
        }
    }
}

module.exports = {
    createDriver
}